import type { UserSubscriptionResponse } from "~/types/payment";
import type { MeResponse } from "~/types/profile";

type FullstoryProperties = Record<string, string | number | boolean>;
export type FullstorySubscriptionContext = {
  isSubscriber?: boolean;
  subscriptionPlan?: string;
};

type FullstoryIdentityPayload = {
  anonymous?: boolean;
  uid?: string;
  properties?: FullstoryProperties;
};

type FullstoryTrackEventPayload = {
  name: string;
  properties?: FullstoryProperties;
};

type FullstoryApi = {
  (event: "setIdentity", payload: FullstoryIdentityPayload): void;
  (event: "trackEvent", payload: FullstoryTrackEventPayload): void;
};

declare global {
  interface Window {
    FS?: FullstoryApi;
  }
}

let lastIdentityPayload = "";
const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active", "pending"]);

function hasTemporarySubscriptionAccess(
  subscription: Pick<UserSubscriptionResponse, "free_day_access_until">,
) {
  if (!subscription.free_day_access_until) {
    return false;
  }

  const freeDayUntil = new Date(subscription.free_day_access_until).getTime();
  return !Number.isNaN(freeDayUntil) && freeDayUntil > Date.now();
}

function getFullstoryApi() {
  if (!import.meta.client || typeof window.FS !== "function") {
    return null;
  }

  return window.FS;
}

function getPrimaryRole(user: MeResponse) {
  if (user.is_superuser) return "superuser";
  if (user.is_staff) return "staff";
  if (user.is_teacher) return "teacher";
  if (user.is_student) return "student";

  return "user";
}

export function getFullstorySubscriptionContext(
  subscription: UserSubscriptionResponse,
): FullstorySubscriptionContext {
  const properties: FullstorySubscriptionContext = {
    isSubscriber:
      ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.status) ||
      hasTemporarySubscriptionAccess(subscription),
  };

  const subscriptionPlan = subscription.subscription_plan?.name?.trim();

  if (subscriptionPlan) {
    properties.subscriptionPlan = subscriptionPlan;
  }

  return properties;
}

function getIdentityPayload(
  user: MeResponse,
  subscriptionContext?: FullstorySubscriptionContext,
) {
  const displayName = `${user.first_name} ${user.last_name}`.trim();
  const properties: FullstoryProperties = {
    primaryRole: getPrimaryRole(user),
  };

  if (typeof subscriptionContext?.isSubscriber === "boolean") {
    properties.isSubscriber = subscriptionContext.isSubscriber;
  }

  if (subscriptionContext?.subscriptionPlan) {
    properties.subscriptionPlan = subscriptionContext.subscriptionPlan;
  }

  if (displayName) {
    properties.displayName = displayName;
  }

  if (user.email) {
    properties.email = user.email;
  }

  return {
    uid: `teachplus_user_${user.id}`,
    properties,
  };
}

export function identifyFullstoryUser(
  user: MeResponse,
  subscriptionContext?: FullstorySubscriptionContext,
) {
  const fullstory = getFullstoryApi();

  if (!fullstory || !user.id) {
    return;
  }

  const payload = getIdentityPayload(user, subscriptionContext);
  const serializedPayload = JSON.stringify(payload);

  if (serializedPayload === lastIdentityPayload) {
    return;
  }

  fullstory("setIdentity", payload);
  lastIdentityPayload = serializedPayload;
}

export function anonymizeFullstoryUser() {
  const fullstory = getFullstoryApi();

  lastIdentityPayload = "";

  if (!fullstory) {
    return;
  }

  fullstory("setIdentity", { anonymous: true });
}

export function trackFullstoryEvent(
  name: string,
  properties?: FullstoryProperties,
) {
  const fullstory = getFullstoryApi();

  if (!fullstory) {
    return;
  }

  fullstory("trackEvent", {
    name,
    properties,
  });
}
